import React, { useCallback, useState } from 'react';
import { CollectionAnalysisOwnProps } from 'configuration-schemas/ownProps';
import { useDispatch, useSelector } from 'react-redux';
import InputGeneric from 'src/common-ui/components/Inputs/InputGeneric/InputGeneric';
import Overlay from 'src/common-ui/components/Overlay/Overlay';
import Subheader from 'src/components/Subheader/Subheader.container';
import { isViewDefnLoaded } from 'src/dao/tenantConfigClient';
import { styles } from 'src/pages/AssortmentBuild/CollectionAnalysis/CollectionAnalysis.styles';
import { isDataLoaded } from 'src/services/pivotServiceCache';
import {
  selectAnchorDataState,
  selectAnchorGridConfig,
  selectClusterDataState,
  selectClusterGridConfig,
  selectCollectionAnalysisAnchorData,
  selectCollectionAnalysisClusterData,
  selectCollectionDataState,
  selectFormattedCollectionData,
  selectFormattedItemsData,
  selectFormattedMacroData,
  selectItemsDataState,
  selectCollectionUpdateConfig,
  selectDescriptionViewConfig,
  selectMacroType,
  selectNameViewConfig,
  selectViewDefnState,
} from './CollectionAnalysis.selectors';
import { Divider } from '@material-ui/core';
import { KeyFinancialsAccordion } from 'src/common-ui/components/Macros/KeyFinancial/KeyFinancialsAccordion';
import { MacrosProps } from 'src/common-ui/components/Macros/Macros.types';
import { ChoiceProductivityAccordion } from 'src/common-ui/components/Macros/ChoiceProductivity/ChoiceProductivityAccordion';
import { MacroTypes } from 'configuration-schemas/viewdefns/literals';
import EditableGrid, { EditableGridProps } from 'src/components/ConfigurableGrid/EditableGrid/EditableGrid';
import { StyledAccordion } from 'src/components/StyledAccordion/StyledAccordion';
import { isEmpty, noop } from 'lodash';
import { MacroDescription } from 'src/common-ui/components/Macros/MacroDescription/MacroDescription';
import type { BasicViewItem, MassEditCoordinateMap } from 'configuration-schemas/viewdefns/general';
import { BasicPivotItem } from 'src/worker/pivotWorker.types';
import { generateCoordinateValues } from 'src/components/ConfigurableGrid/utils/ConfigurableGrid.utils';
import { EditCoordinates, GranularEditPayloadItem } from 'src/dao/pivotClient';
import ServiceContainer from 'src/ServiceContainer';

interface CollectionAnalysisMacroProps {
  macros: MacrosProps[];
  macroType: MacroTypes;
}

// this is largely copied from the one in `EditableGrid` but cut down to bare bones editing
const submitGranularUpdate = async (
  data: BasicPivotItem,
  value: unknown,
  viewConfig: BasicViewItem,
  staticCoordinateMap?: MassEditCoordinateMap,
) => {
  if (isEmpty(staticCoordinateMap)) {
    return;
  }

  const localCoordinateMap = staticCoordinateMap;
  const modifiedValue = value;
  const { dataIndex } = viewConfig;

  let itemPayload: GranularEditPayloadItem = { coordinates: {} as EditCoordinates };
  const itemToUpdate: BasicPivotItem = { [dataIndex]: modifiedValue } as BasicPivotItem;
  const coordinates: EditCoordinates = generateCoordinateValues(localCoordinateMap, data);
  itemPayload = {
    ...itemToUpdate,
    coordinates,
  };

  // submit data
  await ServiceContainer.pivotService.granularEditSubmitData([itemPayload]);
};

function CollectionAnalysisMacro({ macros, macroType }: CollectionAnalysisMacroProps) {
  switch (macroType) {
    case 'choiceProductivity':
      return <ChoiceProductivityAccordion dataLoaded={true} macros={macros} />;
    case 'keyFinancials':
      return <KeyFinancialsAccordion dataLoaded={true} macros={macros} />;
    case 'macroDescription':
      return <MacroDescription dataLoaded={true} macros={macros} />;
    default:
      return <span>Unknown Macro Component</span>;
  }
}

interface CollectionAnalysisProps extends CollectionAnalysisOwnProps {}

export function CollectionAnalysis(_props: CollectionAnalysisProps) {
  const viewDefnState = useSelector(selectViewDefnState);
  const collectionDataState = useSelector(selectCollectionDataState);
  const itemsDataState = useSelector(selectItemsDataState);
  const anchorDataState = useSelector(selectAnchorDataState);
  const clusterDataState = useSelector(selectClusterDataState);

  const anchorData = useSelector(selectCollectionAnalysisAnchorData);
  const { colDefs: anchorColumnDefs, headerText: anchorHeaderText } = useSelector(selectAnchorGridConfig);
  const clusterData = useSelector(selectCollectionAnalysisClusterData);
  const { colDefs: clusterColumnDefs, headerText: clusterHeaderText } = useSelector(selectClusterGridConfig);

  const collectionData = useSelector(selectFormattedCollectionData);
  const { image, description, name, metrics } = collectionData;
  const nameViewConfig = useSelector(selectNameViewConfig);
  const descriptionViewConfig = useSelector(selectDescriptionViewConfig);
  const { updateCoordinateMap } = useSelector(selectCollectionUpdateConfig);
  const [editedName, setEditedName] = useState<string | null>(null);
  const [editedDescription, setEditedDescription] = useState<string | null>(null);

  const handleFieldBlur = useCallback(
    (viewConfig: BasicViewItem, setter: React.Dispatch<React.SetStateAction<string | null>>, value: string) => {
      setter(value);
      if (viewConfig) {
        submitGranularUpdate(
          collectionData as unknown as BasicPivotItem,
          value,
          viewConfig,
          updateCoordinateMap as MassEditCoordinateMap,
        );
      }
    },
    [collectionData, updateCoordinateMap],
  );
  const handleNameBlur = useCallback(
    (value: string) => {
      if (nameViewConfig) {
        handleFieldBlur(nameViewConfig, setEditedName, value);
      }
    },
    [nameViewConfig, handleFieldBlur],
  );
  const handleDescriptionBlur = useCallback(
    (value: string) => {
      if (descriptionViewConfig) {
        handleFieldBlur(descriptionViewConfig, setEditedDescription, value);
      }
    },
    [descriptionViewConfig, handleFieldBlur],
  );
  const formattedItems = useSelector(selectFormattedItemsData);
  const macros = useSelector(selectFormattedMacroData);
  const macroType = useSelector(selectMacroType);

  const configLoaded = isViewDefnLoaded(viewDefnState);
  const loading =
    !configLoaded ||
    !isDataLoaded(collectionDataState) ||
    !isDataLoaded(itemsDataState) ||
    !isDataLoaded(anchorDataState) ||
    !isDataLoaded(clusterDataState);

  const anchorGridProps: EditableGridProps = {
    configLoaded,
    dataLoaded: !loading,
    data: anchorData,
    columnDefs: anchorColumnDefs,
    handleGridReady: noop,
    // TODO: will need to handle breadcrumb restore + scrolling in this function
    onFirstDataRendered: noop,
  } as EditableGridProps;

  const clusterGridProps: EditableGridProps = {
    configLoaded,
    dataLoaded: !loading,
    data: clusterData,
    columnDefs: clusterColumnDefs,
    handleGridReady: noop,
    // TODO: will need to handle breadcrumb restore + scrolling in this function
    onFirstDataRendered: noop,
  } as EditableGridProps;

  return (
    <section className={styles}>
      <Overlay type="loading" fitParent={true} visible={loading} />
      <div className="collection-info">
        <div className="collection-header">
          <InputGeneric
            containerClassName="label"
            editable={Boolean((nameViewConfig as Record<string, unknown>)?.editable)}
            autoFocus={false}
            value={editedName ?? name ?? 'placeholder'}
            valid={true}
            dataQa="collection-analysis-name"
            onBlur={handleNameBlur}
          />
          <InputGeneric
            containerClassName="label"
            editable={Boolean((descriptionViewConfig as Record<string, unknown>)?.editable)}
            autoFocus={false}
            value={editedDescription ?? description}
            valid={true}
            dataQa="collection-analysis-description"
            onBlur={handleDescriptionBlur}
          />
        </div>
        <div className="collection-metrics">
          {metrics?.map((metric, index) => {
            const { text, value } = metric;
            return (
              <div key={`${text}-${index}`}>
                <span className="metric-header">{`${text}:`}</span>
                <span className="metric-value">{value ?? 'Placeholder'}</span>
                {/* FIXME: may need some status Badge renderer here, keeping it text based for now */}
              </div>
            );
          })}
        </div>
        <img src={image} />
        <header className="items-header">
          <span>Items in Look {`(${formattedItems.length})`}</span>
        </header>
        <div className="detail-cards">
          {formattedItems.length > 0 ? (
            <React.Fragment>
              {formattedItems.map((card, index) => (
                <img key={index} src={card.imgSrc} />
              ))}
            </React.Fragment>
          ) : (
            <div className="no-data">No Data</div>
          )}
        </div>
      </div>
      <Divider className="divider" orientation="vertical" flexItem={true} />
      <div className="analysis-info">
        <div className="macros">
          {macroType ? <CollectionAnalysisMacro macros={macros} macroType={macroType} /> : null}
        </div>
        <div className="analysis-grid">
          <StyledAccordion expanded={true} title={anchorHeaderText}>
            <EditableGrid {...anchorGridProps} />
          </StyledAccordion>
        </div>
        <div className="analysis-grid">
          <StyledAccordion expanded={true} title={clusterHeaderText}>
            <EditableGrid {...clusterGridProps} />
          </StyledAccordion>
        </div>
      </div>
    </section>
  );
}
